import {
  CheckPatientEligibility,
  CreateEligibilityBenefitEditRequestBody,
  DeleteAllCoverageEligibilityBenefitEditRequestBody,
  DeleteEligibilityBenefitEditRequestBody,
  DeleteManualPayerBenefit,
  EligibilityBenefitEditRequestTypes,
  EligibilityBenefitEditResponse,
  UpsertEligibilityBenefitEditRequestBody,
  UpsertPayerBenefitManually
} from '@app/core/models/eligibility.model';
import {
  CreateEligibilityBenefitEditResponse,
  GetEligibilityByAppointmentIdResponse,
  GetEligibilityInsuranceByAppointmentIdResponse
} from '@app/core/models/responses/eligibility.response';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpMethod, HttpService } from '../http/http.service';
import { Subject, catchError, throwError } from 'rxjs';

import { APIConstants } from '@app/core/constants/api.constants';
import { EmbedAuthService } from '../auth/embed-auth.service';
import { EmbedMessageToParentStatus } from '@app/core/models/embed.model';
import { GetAppointmentByIdResponse } from '@app/core/models/responses';
import { Injectable } from '@angular/core';
import { PatientRecord } from '@app/core/models/patients.model';
import { SharedMethods } from '@app/shared/utils/shared-methods';

export type OpenEligbilityEventPayload =
  | {
      type: 'NEW';
    }
  | {
      type: 'EDIT';
      data: GetAppointmentByIdResponse;
    }
  | {
      type: 'ADD_APPOINTMENT';
      data: PatientRecord;
    };

export const EligbilityFormType = {
  NEW: 'NEW',
  EDIT: 'EDIT',
  ADD_APPOINTMENT: 'ADD_APPOINTMENT'
};

@Injectable({
  providedIn: 'root'
})
export class EligibilityService {
  private $eligibilityCheckChange = new Subject<number>();

  constructor(private _http: HttpService, private embedAuthService: EmbedAuthService) {}

  get onEligibilityCheckChange$() {
    return this.$eligibilityCheckChange.asObservable();
  }

  emitEligibilityChange(appointmentId: number) {
    this.$eligibilityCheckChange.next(appointmentId);
  }

  getEligibilityOfAppointment(appointmentId: number, eligibilityId?: number) {
    let params = new HttpParams({ fromObject: { appointmentId } });

    if (eligibilityId) {
      params = params.set('eligibilityId', eligibilityId);
    }

    return this._http
      .sendSecureRequest<GetEligibilityByAppointmentIdResponse>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedEligibility
          : APIConstants.eligibilityBaseUrl,
        params
      })
      .pipe(
        catchError((error) => {
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getEligibilityInsuranceOfAppointment(appointmentId: number, eligibilityId?: number) {
    let params = new HttpParams({ fromObject: { appointmentId } });

    if (eligibilityId) {
      params = params.set('eligibilityId', eligibilityId);
    }

    const baseUrl = this.embedAuthService.isEmbedded()
      ? APIConstants.externalApiEmbedEligibility
      : APIConstants.eligibilityBaseUrl;
    return this._http
      .sendSecureRequest<GetEligibilityInsuranceByAppointmentIdResponse>({
        method: HttpMethod.GET,
        url: `${baseUrl}/insurance`,
        params
      })
      .pipe(
        catchError((error) => {
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getEligibilityStatusOfAppointment(appointmentId: number, eligibilityId?: number) {
    let params = new HttpParams({ fromObject: { appointmentId } });

    if (eligibilityId) {
      params = params.set('eligibilityId', eligibilityId);
    }

    return this._http.sendSecureRequest<GetEligibilityByAppointmentIdResponse>({
      method: HttpMethod.GET,
      url: APIConstants.getEligibilityStatus,
      params
    });
  }

  //New eligibility check
  checkPatientEligibility(data: CheckPatientEligibility) {
    return this._http.sendSecureRequest<any>({
      method: HttpMethod.POST,
      headers: new HttpHeaders(),
      url: APIConstants.checkPatientEligibility,
      body: data
    });
  }

  //Sync appointment  eligibility check
  syncAppointmentEligibility(appointmentId: number) {
    return this._http.sendSecureRequest<any>({
      method: HttpMethod.POST,
      headers: new HttpHeaders(),
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedReSyncEligibilityOfAppointment
        : APIConstants.reSyncEligibilityOfAppointment,
      body: { appointmentId: appointmentId }
    });
  }

  // Create or Edit benefit data manually
  upsertPayerBenefit(benefit: UpsertPayerBenefitManually) {
    return this._http.sendSecureRequest<any>({
      method: HttpMethod.POST,
      headers: new HttpHeaders(),
      url: APIConstants.payerBenefitManualEntry,
      body: benefit
    });
  }

  // Delete eligibility edit
  deleteEligibilityEdit(
    req: {
      eligibilityId: number;
      benefitType: EligibilityBenefitEditRequestTypes;
      body:
        | DeleteAllCoverageEligibilityBenefitEditRequestBody
        | DeleteEligibilityBenefitEditRequestBody;
    },
    isEmbedded = false
  ) {
    return this._http.sendSecureRequest<any>({
      method: HttpMethod.DELETE,
      headers: new HttpHeaders(),
      url:
        `${
          !isEmbedded ? APIConstants.eligibilityBaseUrl : APIConstants.externalApiEmbedEligibility
        }/${req.eligibilityId}` + `${APIConstants.eligibilityBenefitEditUrl}/${req.benefitType}`,
      body: req.body
    });
  }

  // Get manual benefit data
  getPayerBenefit(eligibilityId: number) {
    return this._http.sendSecureRequest<any>({
      method: HttpMethod.GET,
      headers: new HttpHeaders(),
      url: APIConstants.payerBenefitManualEntry,
      params: new HttpParams({ fromObject: { eligibilityId } })
    });
  }

  createEligibilityBenefitEdit(
    req: {
      eligibilityId: number;
      benefitType: EligibilityBenefitEditRequestTypes;
      body: CreateEligibilityBenefitEditRequestBody;
    },
    isEmbedded = false
  ) {
    return this._http.sendSecureRequest<CreateEligibilityBenefitEditResponse>({
      method: HttpMethod.POST,
      headers: new HttpHeaders(),
      url:
        `${
          !isEmbedded ? APIConstants.eligibilityBaseUrl : APIConstants.externalApiEmbedEligibility
        }/${req.eligibilityId}` + `${APIConstants.eligibilityBenefitEditUrl}/${req.benefitType}`,
      body: req.body
    });
  }

  upsertEligibilityBenefitEdit(
    req: {
      eligibilityId: number;
      benefitType: EligibilityBenefitEditRequestTypes;
      body: UpsertEligibilityBenefitEditRequestBody;
    },
    isEmbedded = false
  ) {
    return this._http.sendSecureRequest({
      method: HttpMethod.PUT,
      headers: new HttpHeaders(),
      url:
        `${
          !isEmbedded ? APIConstants.eligibilityBaseUrl : APIConstants.externalApiEmbedEligibility
        }/${req.eligibilityId}` + `${APIConstants.eligibilityBenefitEditUrl}/${req.benefitType}`,
      body: req.body
    });
  }

  getEligibilityEdits(eligibilityId: number, isEmbedded = false) {
    return this._http.sendSecureRequest<EligibilityBenefitEditResponse>({
      method: HttpMethod.GET,
      headers: new HttpHeaders(),
      url:
        `${
          !isEmbedded ? APIConstants.eligibilityBaseUrl : APIConstants.externalApiEmbedEligibility
        }/${eligibilityId}` + `${APIConstants.eligibilityBenefitEditUrl}`
    });
  }
}
